import React from 'react';
import Loader from '../../Components/common/loader/loader';
import SummaryGatewayInfoHeader from './SummaryGatewayInfoHeader';
import SummarySortIcon from './SummarySortIcon';
import SummaryTableRow from './SummaryTableRow';
import { SortableKeys, SummarySortConfig } from './summaryTypes';

type Props = {
  activeTab: 'assets' | 'pinned' | 'mapview';
  loading: boolean;
  pinnedPageLoading: boolean;
  pagedAssetSummary: Record<string, any>[];
  pagedPinnedRows: Record<string, any>[];
  pinnedAssetsMap: Record<string, unknown>;
  pinningAssetId: string | null;
  sortConfig: SummarySortConfig | null;
  pinnedSortConfig: SummarySortConfig | null;
  onRequestSort: (key: SortableKeys) => void;
  onPinClick: (row: Record<string, any>) => void;
  onAssetNameClick: (row: Record<string, any>) => void;
  onGatewayStatusClick: (row: Record<string, any>) => void;
  onAlertsClick: (row: Record<string, any>) => void;
};

const renderSortIcon = (
  columnKey: SortableKeys,
  props: Pick<Props, 'activeTab' | 'sortConfig' | 'pinnedSortConfig' | 'onRequestSort'>
) => (
  <SummarySortIcon
    columnKey={columnKey}
    activeTab={props.activeTab}
    sortConfig={props.sortConfig}
    pinnedSortConfig={props.pinnedSortConfig}
    onRequestSort={props.onRequestSort}
  />
);

const SummaryTabTables: React.FC<Props> = (props) => {
  const sortProps = {
    activeTab: props.activeTab,
    sortConfig: props.sortConfig,
    pinnedSortConfig: props.pinnedSortConfig,
    onRequestSort: props.onRequestSort,
  };

  if (props.activeTab === 'assets') {
    return (
      <div className="summary-table-scroll">
        <table className="global-table summary-table summary-sticky" style={{ overflow: 'scroll' }}>
          <thead style={{ position: 'sticky', top: '0', zIndex: '100' }}>
            <tr>
              <th className="col-narrow" style={{ zIndex: '5' }} />
              <th className="col-large-assetname" onClick={() => props.onRequestSort('assetName')}>
                <div className="sort-header">
                  <p className="data-text">Asset Name</p>
                  {renderSortIcon('assetName', sortProps)}
                </div>
              </th>
              <th className="col-customer-summary" onClick={() => props.onRequestSort('customer')}>
                <div className="sort-header">
                  <p className="data-text">Customer</p>
                  {renderSortIcon('customer', sortProps)}
                </div>
              </th>
              <th className="col-site-summary" onClick={() => props.onRequestSort('site')}>
                <div className="sort-header">
                  <p className="data-text">Site</p>
                  {renderSortIcon('site', sortProps)}
                </div>
              </th>
              <th className="col-plant-summary" onClick={() => props.onRequestSort('plant')}>
                <div className="sort-header">
                  <p className="data-text">Plant</p>
                  {renderSortIcon('plant', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('assetType')}>
                <div className="sort-header">
                  <p className="data-text">Asset Type</p>
                  {renderSortIcon('assetType', sortProps)}
                </div>
              </th>
              <th className="col-status" onClick={() => props.onRequestSort('status')}>
                <div className="sort-header">
                  <p className="data-text">Asset Status</p>
                  {renderSortIcon('status', sortProps)}
                </div>
              </th>
              <th className="col-medium">
                <div className="sort-header">
                  <SummaryGatewayInfoHeader />
                </div>
              </th>
              <th className="col-alert" onClick={() => props.onRequestSort('alertsCount')}>
                <div className="sort-header">
                  <p className="data-text">Alerts</p>
                  {renderSortIcon('alertsCount', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('lastCommunication')}>
                <div className="sort-header">
                  <p className="data-text">Last Data Received</p>
                  {renderSortIcon('lastCommunication', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('firstCommunication')}>
                <div className="sort-header">
                  <p className="data-text">Commissioned On</p>
                  {renderSortIcon('firstCommunication', sortProps)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.loading ? (
              <tr>
                <td colSpan={9} className="table-loading">
                  <Loader />
                </td>
              </tr>
            ) : props.pagedAssetSummary.length > 0 ? (
              props.pagedAssetSummary.map((row, idx) => (
                <SummaryTableRow
                  key={row.id || `${row.assetId}-${idx}` || `asset-${idx}`}
                  row={row}
                  rowIndex={idx}
                  variant="assets"
                  pinningAssetId={props.pinningAssetId}
                  isPinnedInMap={Boolean(props.pinnedAssetsMap[row.assetId])}
                  onPinClick={props.onPinClick}
                  onAssetNameClick={props.onAssetNameClick}
                  onGatewayStatusClick={props.onGatewayStatusClick}
                  onAlertsClick={props.onAlertsClick}
                />
              ))
            ) : (
              <tr>
                <td colSpan={11} className="table-empty">No Data Available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  if (props.activeTab === 'pinned') {
    return (
      <div className="summary-table-scroll">
        <table className="global-table summary-table summary-sticky" style={{ overflow: 'scroll' }}>
          <thead style={{ position: 'sticky', top: '0', zIndex: '100' }}>
            <tr>
              <th className="col-narrow" />
              <th className="col-large-assetname" onClick={() => props.onRequestSort('assetName')}>
                <div className="sort-header">
                  <p className="data-text">Asset Name</p>
                  {renderSortIcon('assetName', sortProps)}
                </div>
              </th>
              <th className="col-customer-summary" onClick={() => props.onRequestSort('customer')}>
                <div className="sort-header">
                  <p className="data-text">Customer</p>
                  {renderSortIcon('customer', sortProps)}
                </div>
              </th>
              <th className="col-site-summary" onClick={() => props.onRequestSort('site')}>
                <div className="sort-header">
                  <p className="data-text">Site</p>
                  {renderSortIcon('site', sortProps)}
                </div>
              </th>
              <th className="col-plant-summary" onClick={() => props.onRequestSort('plant')}>
                <div className="sort-header">
                  <p className="data-text">Plant</p>
                  {renderSortIcon('plant', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('assetType')}>
                <div className="sort-header">
                  <p className="data-text">Asset Type</p>
                  {renderSortIcon('assetType', sortProps)}
                </div>
              </th>
              <th className="col-status" onClick={() => props.onRequestSort('status')}>
                <div className="sort-header">
                  <p className="data-text"> Asset Status</p>
                  {renderSortIcon('status', sortProps)}
                </div>
              </th>
              <th className="col-medium">
                <div className="sort-header">
                  <SummaryGatewayInfoHeader />
                </div>
              </th>
              <th className="col-alert" onClick={() => props.onRequestSort('alertsCount')}>
                <div className="sort-header">
                  <p className="data-text">Alerts</p>
                  {renderSortIcon('alertsCount', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('lastCommunication')}>
                <div className="sort-header">
                  <p className="data-text">Last Data Received</p>
                  {renderSortIcon('lastCommunication', sortProps)}
                </div>
              </th>
              <th className="col-medium" onClick={() => props.onRequestSort('firstCommunication')}>
                <div className="sort-header">
                  <p className="data-text">Commissioned On</p>
                  {renderSortIcon('firstCommunication', sortProps)}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.pinnedPageLoading ? (
              <tr>
                <td colSpan={11} className="table-loading">
                  <Loader />
                </td>
              </tr>
            ) : props.pagedPinnedRows.length > 0 ? (
              props.pagedPinnedRows.map((row, idx) => (
                <SummaryTableRow
                  key={row.id || `${row.assetId}-${idx}` || `pinned-${idx}`}
                  row={row}
                  rowIndex={idx}
                  variant="pinned"
                  pinningAssetId={props.pinningAssetId}
                  isPinnedInMap={true}
                  onPinClick={props.onPinClick}
                  onAssetNameClick={props.onAssetNameClick}
                  onGatewayStatusClick={props.onGatewayStatusClick}
                  onAlertsClick={props.onAlertsClick}
                />
              ))
            ) : (
              <tr>
                <td colSpan={11}>No Data Available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
};

export default SummaryTabTables;
